import { t } from 'i18next'

import type { TPropostaSchemaInput } from '@/features/proposta/schemas'

export type ErroValidacao = {
  message: string
  path?: string[]
}

const pathComprador = (index: number) => ['compradores', String(index), 'percentualParticipacao']
type Titular = TPropostaSchemaInput['detalhesDaProposta']['participacaoCompradores']['titular']
type TCompradores = TPropostaSchemaInput['detalhesDaProposta']['participacaoCompradores']['compradores']

const IDEAL = 10_000 // 100%

export class ValidadorComprador {
  static validarTitularObrigatorio(
    compradores: Array<{ clienteId: string }>,
    titularId: string | null | undefined,
  ): ErroValidacao | null {
    const haMaisDeUmComprador = compradores.length > 1
    const naoSelecionouTitular = !titularId

    if (haMaisDeUmComprador && naoSelecionouTitular) {
      return {
        path: ['detalhesDaProposta', 'participacaoCompradores', 'titular', 'id'],
        message: t('proposta:detalhes_proposta.participacao_comprador.erros.titular_obrigatorio'),
      }
    }

    return null
  }

  static validarPercentualTitular(compradores: Array<{ clienteId: string }>, percentual: number): ErroValidacao | null {
    const haMaisDeUmComprador = compradores.length > 1
    if (!haMaisDeUmComprador) return null

    if (percentual < 0.01) {
      return {
        path: ['detalhesDaProposta', 'participacaoCompradores', 'titular', 'percentualParticipacao'],
        message: t('proposta:detalhes_proposta.participacao_comprador.erros.maior_zero'),
      }
    }

    if (percentual > 99.99) {
      return {
        path: ['detalhesDaProposta', 'participacaoCompradores', 'titular', 'percentualParticipacao'],
        message: t('proposta:detalhes_proposta.participacao_comprador.erros.menor_cem'),
      }
    }
    return null
  }

  static validarTotalParticipacao(titularPercentual: number, demaisCompradores: TCompradores): ErroValidacao | null {
    const toCent = (v = 0) => Math.round(v * 100)
    const totalCent =
      toCent(titularPercentual) + demaisCompradores.reduce((acc, c) => acc + toCent(c.percentualParticipacao), 0)
    if (totalCent !== IDEAL) return { message: t('proposta:detalhes_proposta.participacao_comprador.erros.soma_total') }
    return null
  }

  static validarParticipacoesCompradores(titular: Titular, demaisCompradores: TCompradores): ErroValidacao[] {
    const erros: ErroValidacao[] = []

    const toCent = (valor = 0) => Math.round(valor * 100)

    const titularCent = toCent(titular?.percentualParticipacao)
    const demaisCent = demaisCompradores.reduce((acc, c) => acc + toCent(c.percentualParticipacao), 0)

    const totalCent = titularCent + demaisCent

    if (totalCent < IDEAL) {
      const percentualFaltante = ((IDEAL - totalCent) / 100).toFixed(2)
      demaisCompradores.forEach((_, i) =>
        erros.push({
          message: t('proposta:detalhes_proposta.participacao_comprador.erros.abaixo_limite', {
            percentual: percentualFaltante.replace('.', ','),
          }),
          path: pathComprador(i),
        }),
      )
    }

    if (totalCent > IDEAL) {
      const percentualExcesso = ((totalCent - IDEAL) / 100).toFixed(2)
      demaisCompradores.forEach((_, i) =>
        erros.push({
          message: t('proposta:detalhes_proposta.participacao_comprador.erros.acima_limite', {
            percentual: percentualExcesso.replace('.', ','),
          }),
          path: pathComprador(i),
        }),
      )
    }

    return erros
  }
}
